import GlobalTuitionDueDate from "~/components/GlobalTuitionDueDate";
import { Form } from "@unform/web";
import {
  Building,
  CirclePlay,
  Contact,
  Edit,
  History,
  Pencil,
  X,
} from "lucide-react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import api from "~/services/api";
import { Zoom, toast } from "react-toastify";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import {
  getPriceLists,
  getRegistries,
  sanitizeSpecialChars,
} from "~/functions";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import { differenceInDays, format, isValid, parseISO } from "date-fns";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { useSelector } from "react-redux";
import { FullGridContext } from "../..";
import {
  genderOptions,
  optionsContactForm,
} from "~/functions/selectPopoverOptions";
import PricesSimulation from "~/components/PricesSimulation";
import PhoneNumberInput from "~/components/RegisterForm/PhoneNumberInput";
import FindGeneric from "~/components/Finds/FindGeneric";
import { Scope, useField } from "@unform/core";
import { useNavigate } from "react-router-dom";
import NewDatepicker from "~/pages/Financial/Outbounds/Payees/ModalFilters/NewDatepicker";
import { AlertContext } from "~/App";
import { mapPlanScheduleOptions } from "~/functions/planSchedules";
import GlobalSessionSummary from "~/components/GlobalSessionSummary";

export const InputContext = createContext({});

function ProspectDateField({
  name,
  title,
  required = false,
  grow = false,
  defaultValue = "",
  InputContext = null,
  onChange = null,
}) {
  const { fieldName, registerField } = useField(name);
  const { setSuccessfullyUpdated } = useContext(InputContext);
  const [value, setValue] = useState(defaultValue || "");
  const fieldRef = useRef({ value: defaultValue || "" });

  useEffect(() => {
    const initialValue = defaultValue || "";
    fieldRef.current.value = initialValue;
    setValue(initialValue);
  }, [defaultValue]);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: fieldRef.current,
      path: "value",
      setValue: (ref, nextValue) => {
        const resolved = nextValue || "";
        ref.value = resolved;
        setValue(resolved);
      },
      clearValue: (ref) => {
        ref.value = "";
        setValue("");
      },
    });
  }, [fieldName, registerField]);

  function handleChange(nextValue) {
    fieldRef.current.value = nextValue || "";
    setValue(nextValue || "");
    setSuccessfullyUpdated(false);
    if (onChange) {
      onChange(nextValue || "");
    }
  }

  return (
    <div
      className={`flex flex-col justify-center items-start ${
        grow ? "grow" : ""
      }`}
    >
      <div className="px-2 text-xs flex flex-row justify-between items-center">
        {title} {required && <span className="text-red-600">*</span>}
      </div>
      <div className="w-full border border-gray-300 rounded-xs px-2 py-1 text-sm">
        <NewDatepicker
          value={value}
          onChange={handleChange}
          className="w-full"
          placeholder="MM/DD/YYYY"
        />
      </div>
    </div>
  );
}

function normalizePhoneForInput(value) {
  if (typeof value !== "string") return undefined;

  const trimmedValue = value.trim();
  if (!trimmedValue) return undefined;
  if (!trimmedValue.startsWith("+")) return trimmedValue;

  const digitsOnly = trimmedValue.slice(1).replace(/\D/g, "");
  return digitsOnly ? `+${digitsOnly}` : undefined;
}

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const navigate = useNavigate();
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
  } = useContext(FullGridContext);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState(null);
  const [subtype, setSubtype] = useState(null);
  const [selectedTypeName, setSelectedTypeName] = useState(null);
  const [selectedSubStatusName, setSelectedSubStatusName] = useState(null);
  const [selectedSubStatusId, setSelectedSubStatusId] = useState(null);
  const [planScheduleOptions, setPlanScheduleOptions] = useState([]);
  const [editSession, setEditSession] = useState(null);
  const [editPlanScheduleOptions, setEditPlanScheduleOptions] = useState(
    []
  );
  const [editHourlyPreview, setEditHourlyPreview] = useState(null);
  const [activeInvoices, setActiveInvoices] = useState([]);
  const [editSimulatedTotals, setEditSimulatedTotals] = useState(null);
  const [paymentType, setPaymentType] = useState("full");
  const [simulatedTotals, setSimulatedTotals] = useState(null);
  // Non-F1 + Global supports two billing modes; the user picks one. "fixed"
  // is the original flow (price-list tuition) and the backend default;
  // "hourly" charges by the hours derived from the plan schedule.
  const [pricingMode, setPricingMode] = useState("fixed");
  // Hourly mode — the backend is the source of truth for hours and tuition.
  // We only track the selected schedule/dates and display the preview
  // returned by POST /contracts/preview-tuition.
  const [planScheduleId, setPlanScheduleId] = useState(null);
  const [sessionStartDate, setSessionStartDate] = useState(null);
  const [firstTuitionDueDate, setFirstTuitionDueDate] = useState("");
  const [sessionEndDate, setSessionEndDate] = useState(null);
  const [hourlyPreview, setHourlyPreview] = useState(null);
  const [pageData, setPageData] = useState({
    name: "",
    last_name: "",
    email: "",
    visa_expiration: null,
    gender: null,
    processtype_id: null,
    processsubstatus_id: null,
    status: "Waiting",
    date_of_birth: null,
    loaded: false,
    discount_id: null,
    discount: null,
    total_tuition: 0,
    total_discount: 0,
    registration_fee: 0,
    books: 0,
    tuition_original_price: 0,
    tuition_in_advance: false,
    searchFields: {
      filial_id: null,
      processtype_id: null,
      processsubstatus_id: null,
    },
    typesOptions: [],
    subtypesOptions: [],
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const generalForm = useRef();
  const { alertBox } = useContext(AlertContext);
  const auth = useSelector((state) => state.auth);
  const { agent: userAgent } = auth?.profile;
  const scrollContainerRef = useRef(null);
  const [partnerAndInfluencer, setPartnerAndInfluencer] = useState(
    userAgent
      ? userAgent.partners_and_influencer
      : {
          id: null,
          partners_name: null,
        }
  );

  const newEnrollmentAvaiable =
    pageData?.contracts?.length === 0 ||
    pageData?.contracts?.every((contract) =>
      ["Terminaded", "Terminated", "Inactive"].includes(contract.status)
    );

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo(0, 0);
    }
  }, [activeMenu]);


  async function getPageData(prospectId = null) {
    if (!prospectId) {
      prospectId = id;
    }
    if (prospectId !== "new") {
      try {
        const { data } = await api.get(`/prospects/${prospectId}`);

        setPageData({
          ...data,
          searchFields: {
            processtype_id: data.processtype_id,
            processsubstatus_id: data.processsubstatus_id,
            filial_id: data.filial_id,
          },
          find_processtype_id: data.processtype_id,
          loaded: true,
        });

        const {
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        } = data;
        const registries = await getRegistries({
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        });
        setRegistry(registries);
      } catch (err) {
        console.log(err);
        // toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    } else {
      setPageData({
        ...pageData,
        loaded: true,
      });
      setFormType("full");
    }
  }

  useEffect(() => {
    getPageData();
  }, []);

  async function updatePriceLists() {
    const fields = pageData.searchFields;
    try {
      const { priceLists, discountLists } = await getPriceLists(fields);
      setPageData((current) =>
        Number(current.searchFields.filial_id) === Number(fields.filial_id) &&
        Number(current.searchFields.processsubstatus_id) === Number(fields.processsubstatus_id)
          ? { ...current, priceLists, discountLists }
          : current
      );
    } catch (err) {
      console.error("Could not load price lists:", err);
    }
  }
  useEffect(() => {
    if (
      pageData.searchFields.filial_id &&
      pageData.searchFields.processtype_id &&
      pageData.searchFields.processsubstatus_id
    ) {
      updatePriceLists();
    }
  }, [
    pageData.searchFields.filial_id,
    pageData.searchFields.processtype_id,
    pageData.searchFields.processsubstatus_id,
  ]);

  const activeContract =
    pageData?.contracts?.find(
      (contract) => contract.sessions && contract.sessions.length > 0
    ) || null;
  const activeSession = activeContract?.sessions?.[0] || null;
  const showEditSessionFields =
    Boolean(activeSession) &&
    activeContract?.processtypes?.name === "Non-F1" &&
    activeContract?.processsubstatuses?.name === "Global";

  useEffect(() => {
    if (!activeSession) {
      setEditSession(null);
      return;
    }
    setEditSession({
      session_start_date: activeSession.session_start_date || "",
      session_end_date: activeSession.session_end_date || "",
      first_tuition_due_date: activeSession.first_tuition_due_date || "",
      plan_schedule_id:
        activeSession.plan_schedule_id || activeSession.plan_schedule || "",
      pricing_mode: activeSession.pricing_mode || "fixed",
      payment_type: activeSession.payment_type || "full",
      tuition_amount:
        activeSession.tuition_amount === null ||
        activeSession.tuition_amount === undefined
          ? ""
          : String(activeSession.tuition_amount),
    });
  }, [activeSession?.id, activeSession?.first_tuition_due_date]);

  useEffect(() => {
    async function loadEditPlanSchedules() {
      const filialId = pageData?.filial_id;
      const subStatusId = activeContract?.processsubstatuses?.id;
      if (!showEditSessionFields || !filialId || !subStatusId) {
        setEditPlanScheduleOptions([]);
        return;
      }
      try {
        const { data } = await api.get(
          `/filials/${filialId}/plan-schedules/${subStatusId}`
        );
        setEditPlanScheduleOptions(mapPlanScheduleOptions(data));
      } catch (err) {
        console.log("Error fetching plan schedules:", err);
        setEditPlanScheduleOptions([]);
      }
    }
    loadEditPlanSchedules();
  }, [
    showEditSessionFields,
    pageData?.filial_id,
    activeContract?.processsubstatuses?.id,
  ]);

  useEffect(() => {
    const filialId = pageData?.filial_id;
    const subStatusId = activeContract?.processsubstatuses?.id;
    if (
      !showEditSessionFields ||
      editSession?.pricing_mode !== "hourly" ||
      !filialId ||
      !subStatusId ||
      !editSession?.plan_schedule_id ||
      !editSession?.session_start_date ||
      !editSession?.session_end_date
    ) {
      setEditHourlyPreview(null);
      return;
    }

    let cancelled = false;
    async function loadPreview() {
      try {
        const { data } = await api.post(`/contracts/preview-tuition`, {
          filial_id: filialId,
          processsubstatus_id: subStatusId,
          plan_schedule_id: editSession.plan_schedule_id,
          session_start_date: editSession.session_start_date,
          session_end_date: editSession.session_end_date,
        });
        if (!cancelled) setEditHourlyPreview(data);
      } catch (err) {
        if (!cancelled) setEditHourlyPreview(null);
        console.log("Error fetching tuition preview:", err);
      }
    }
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [
    showEditSessionFields,
    pageData?.filial_id,
    activeContract?.processsubstatuses?.id,
    editSession?.pricing_mode,
    editSession?.plan_schedule_id,
    editSession?.session_start_date,
    editSession?.session_end_date,
  ]);

  const loadActiveInvoices = useCallback(async () => {
    const enrollmentId = activeContract?.enrollment_id;
    if (!showEditSessionFields || !enrollmentId) {
      setActiveInvoices([]);
      return;
    }
    try {
      const { data } = await api.get(`/enrollments/${enrollmentId}/invoices`);
      setActiveInvoices(data?.receivables || []);
    } catch (err) {
      console.log("Error loading global invoices:", err);
      setActiveInvoices([]);
    }
  }, [showEditSessionFields, activeContract?.enrollment_id]);

  useEffect(() => {
    loadActiveInvoices();
  }, [loadActiveInvoices]);

  function setEditSessionField(field, value) {
    setEditSession((prev) => ({ ...(prev || {}), [field]: value }));
    setSuccessfullyUpdated(false);
  }

  async function submitSessionUpdate(prices) {
    const isHourly = editSession.pricing_mode === "hourly";
    const toAmount = (value) =>
      value ? parseFloat(String(value).replace(",", ".")) : 0;
    try {
      await api.put(`/contracts/${activeContract.id}/session`, {
        contractsession_id: activeSession.id,
        session_start_date: editSession.session_start_date,
        session_end_date: editSession.session_end_date,
        first_tuition_due_date: editSession.first_tuition_due_date || null,
        plan_schedule:
          editPlanScheduleOptions.find(
            (option) => option.value === editSession.plan_schedule_id
          )?.label ||
          editSession.plan_schedule_id ||
          null,
        plan_schedule_id: isHourly ? editSession.plan_schedule_id : null,
        pricing_mode: isHourly ? "hourly" : "fixed",
        payment_type: isHourly ? "full" : editSession.payment_type || "full",
        prices,
        registration_fee_amount: toAmount(prices?.registration_fee),
        ...(isHourly
          ? {}
          : { tuition_amount: toAmount(prices?.tuition_original_price) }),
        tuition_discount: isHourly ? 0 : toAmount(prices?.total_discount),
      });
      await getPageData();
      await loadActiveInvoices();
      setSuccessfullyUpdated(true);
      toast("Session updated and invoices regenerated!", { autoClose: 2000 });
    } catch (err) {
      toast(
        err?.response?.data?.error || "Could not update the session.",
        { type: "error", autoClose: 4000 }
      );
    }
  }

  function handleEditSessionSubmit(prices) {
    if (!editSession?.session_start_date || !editSession?.session_end_date) {
      toast.error("Session Start Date and Session End Date are required.");
      return;
    }
    if (
      editSession.pricing_mode === "hourly" &&
      !editSession.plan_schedule_id
    ) {
      toast.error("Plan Schedule is required for hourly billing.");
      return;
    }
    if (!activeContract?.id) {
      toast.error("Active contract not found.");
      return;
    }

    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Saving will cancel the current tuition invoices and generate new ones from these values. This cannot be undone. Continue?",
      buttons: [
        { title: "No", class: "cancel", onPress: () => null },
        { title: "Yes", onPress: () => submitSessionUpdate(prices) },
      ],
    });
  }

  // Show Session/Plan Schedule fields only for a Non-F1 process type with a Global sub status
  const showSessionFields =
    selectedTypeName === "Non-F1" && selectedSubStatusName === "Global";

  useEffect(() => {
    async function loadPlanSchedules() {
      const filialId = pageData.searchFields.filial_id;
      if (!filialId || !selectedSubStatusId || !showSessionFields) {
        setPlanScheduleOptions([]);
        return;
      }
      try {
        const { data } = await api.get(
          `/filials/${filialId}/plan-schedules/${selectedSubStatusId}`
        );
        setPlanScheduleOptions(mapPlanScheduleOptions(data));
      } catch (err) {
        console.log("Error fetching plan schedules:", err);
        setPlanScheduleOptions([]);
      }
    }
    loadPlanSchedules();
  }, [
    pageData.searchFields.filial_id,
    selectedSubStatusId,
    showSessionFields,
  ]);

  // Reset the hourly tracking/preview whenever we leave the Global flow.
  useEffect(() => {
    if (!showSessionFields) {
      setPricingMode("fixed");
      setPlanScheduleId(null);
      setSessionStartDate(null);
      setSessionEndDate(null);
      setHourlyPreview(null);
    }
  }, [showSessionFields]);

  // Non-F1 + Global: ask the backend for the hourly tuition preview. The
  // frontend never computes hours or money locally — what the user sees here
  // is exactly what the backend will charge. This endpoint only calculates,
  // it does not create anything, so it is safe to call on every change.
  useEffect(() => {
    const filialId = pageData.searchFields.filial_id;
    if (
      !showSessionFields ||
      pricingMode !== "hourly" ||
      !filialId ||
      !selectedSubStatusId ||
      !planScheduleId ||
      !sessionStartDate ||
      !sessionEndDate
    ) {
      setHourlyPreview(null);
      return;
    }

    let cancelled = false;
    async function loadPreview() {
      try {
        const { data } = await api.post(`/contracts/preview-tuition`, {
          filial_id: filialId,
          processsubstatus_id: selectedSubStatusId,
          plan_schedule_id: planScheduleId,
          session_start_date: sessionStartDate || null,
          session_end_date: sessionEndDate || null,
        });
        if (!cancelled) {
          setHourlyPreview(data);
        }
      } catch (err) {
        if (!cancelled) {
          setHourlyPreview(null);
        }
        console.log("Error fetching tuition preview:", err);
      }
    }
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [
    showSessionFields,
    pricingMode,
    pageData.searchFields.filial_id,
    selectedSubStatusId,
    planScheduleId,
    sessionStartDate,
    sessionEndDate,
  ]);

  function handlePreferredContactOpen() {
    setTimeout(() => {
      const container = scrollContainerRef.current;
      const selectEl = document.getElementById("preferred_contact_form");
      if (!container || !selectEl) return;

      const menu = container.querySelector(".react-select__menu");
      const menuHeight = menu ? menu.getBoundingClientRect().height : 240;

      const selectRect = selectEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const delta = selectRect.bottom + menuHeight - containerRect.bottom;

      if (delta > 0) {
        container.scrollBy({ top: delta + 20, behavior: "smooth" });
      }
    }, 10);
  }

  async function handleGeneralFormSubmit(data) {
    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      return;
    }
    delete data.status;
    ["name", "last_name"].forEach((field) => {
      if (typeof data[field] === "string") {
        data[field] = sanitizeSpecialChars(data[field], "person").trim();
      }
    });
    if (typeof data.email === "string") {
      data.email = sanitizeSpecialChars(data.email, "email");
    }
    if (data.email && (data.email !== data.email.toLowerCase() || data.email !== data.email.trim())) {
      toast("Email must be lowercase and have no leading/trailing spaces.", {
        type: "warning",
        autoClose: 4000,
      });
      return;
    }
    if (id === "new") {
      try {
        const response = await api.post(`/prospects`, data);
        getPageData(response.data.id);
        setSuccessfullyUpdated(true);
        handleOpened(null);
        // setTimeout(() => {
        //   setActiveMenu("new-enrollment");
        // }, 2000);
        toast("Saved!", { autoClose: 1000 });
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    } else if (activeMenu === "new-enrollment") {
      try {
        const {
          agent,
          processtypes,
          processsubstatuses,
          prices,
          partners_and_influencers,
          session_start_date,
          session_end_date,
          plan_schedule,
        } = data;

        if (showSessionFields) {
          if (!session_start_date || !session_end_date) {
            toast.error(
              "Session Start Date and Session End Date are required."
            );
            return;
          }
          if (pricingMode === "hourly" && !plan_schedule && !planScheduleId) {
            toast.error("Plan Schedule is required for hourly billing.");
            return;
          }
        }

        const registrationFeeAmount = prices?.registration_fee
          ? parseFloat(String(prices.registration_fee).replace(",", "."))
          : 0;
        const tuitionAmount = prices?.tuition_original_price
          ? parseFloat(String(prices.tuition_original_price).replace(",", "."))
          : 0;
        const tuitionDiscountAmount = prices?.total_discount
          ? parseFloat(String(prices.total_discount).replace(",", "."))
          : 0;

        const response = await api.post(
          `/contracts${showSessionFields ? "?global=true" : ""}`,
          {
            student_id: id,
            agent,
            prices: prices,
            processtypes: processtypes,
            processsubstatuses: processsubstatuses,
            partners_and_influencers: partners_and_influencers,
            ...(showSessionFields && {
              session_start_date: session_start_date || null,
              session_end_date: session_end_date || null,
              first_tuition_due_date: data.first_tuition_due_date || firstTuitionDueDate || null,
              plan_schedule:
                planScheduleOptions.find(
                  (option) => option.value === (plan_schedule || planScheduleId)
                )?.label ||
                plan_schedule ||
                null,
              pricing_mode: pricingMode,
              ...(pricingMode === "hourly" && {
                plan_schedule_id: plan_schedule || planScheduleId || null,
                payment_type: "full",
              }),
              ...(pricingMode === "fixed" && {
                payment_type: paymentType,
              }),
              registration_fee_amount: registrationFeeAmount,
              tuition_amount: tuitionAmount,
              tuition_discount: tuitionDiscountAmount,
            }),
          }
        );
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        handleOpened(null);
        navigate(`/Commercial/Enrollments?id=${response.data.enrollment_id}`);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
      return;
    } else if (activeMenu === "edit-enrollment") {
      if (showEditSessionFields) {
        handleEditSessionSubmit(data.prices);
      } else if (data.prices) {
        setLoading(true);
        try {
          await api.put(`/prospects/${id}`, {
            prices: data.prices,
            filial: { id: pageData.filial_id },
            agent: { id: pageData.agent_id || null },
            processtypes: { id: pageData.processtype_id || null },
            processsubstatuses: { id: pageData.processsubstatus_id || null },
            partners_and_influencers: {
              id: pageData.partners_and_influencer_id || null,
            },
          });
          await getPageData();
          setSuccessfullyUpdated(true);
          toast.success("Discounts saved. Open Enrollments to update existing invoices.");
        } catch (err) {
          toast.error(err?.response?.data?.error || "Could not save discounts.");
        } finally {
          setLoading(false);
        }
      }
      return;
    } else if (id !== "new") {
      try {
        await api.put(`/prospects/${id}`, data);
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        handleOpened(null);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    }
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  async function handleInactivate() {
    try {
      await api.delete(`students/${id}`);
      toast("Group Inactivated!", { autoClose: 1000 });
      handleOpened(null);
    } catch (err) {
      toast(err.response.data.error, { type: "error", autoClose: 3000 });
    }
  }

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        formType === "preview" ? (
          <div className="border h-full rounded-xl overflow-hidden flex flex-col justify-start gap-1 overflow-y-scroll">
            <div className="relative bg-gray-100 h-16 px-4 py-2 flex flex-row items-start justify-start">
              <button
                onClick={() => setFormType("full")}
                className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <Pencil size={16} color="#fff" /> Open
              </button>
              <button
                onClick={() => handleOpened(null)}
                className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <X size={16} /> Close
              </button>
              <h2 style={{ fontSize: 24 }}>{pageData.name}</h2>
            </div>
            <div className="flex flex-1 flex-col items-left px-4 py-2 gap-1">
              <p className="border-b mb-1 pb-1">Prospect Information</p>
              <div className="flex flex-row items-center gap-1 text-xs">
                <strong>Name:</strong> {pageData.name}
              </div>
              {(() => {
                const session = pageData?.contracts?.[0]?.sessions?.[0];
                if (!session) return null;
                if (session.pricing_mode !== "hourly") return null;

                const totalHours = session.total_hours ?? 0;
                const ratePerHour = session.rate_per_hour ?? 0;
                const tuitionAmount = session.tuition_amount ?? 0;
                const formatSessionDate = (value) => {
                  if (!value) return "-";
                  const parsed = parseISO(value);
                  return isValid(parsed)
                    ? format(parsed, "MM/dd/yyyy")
                    : value;
                };
                return (
                  <div className="mt-2 flex flex-col gap-1 text-xs bg-blue-50 border border-blue-200 rounded-md p-3">
                    <p className="font-semibold text-gray-700">
                      Global Session (Hourly)
                    </p>
                    <div className="flex flex-row items-center gap-1">
                      <strong>Period:</strong>{" "}
                      {formatSessionDate(session.session_start_date)} →{" "}
                      {formatSessionDate(session.session_end_date)}
                    </div>
                    <div className="flex flex-row items-center gap-1">
                      <strong>Total hours:</strong> {totalHours}
                    </div>
                    <div className="flex flex-row items-center gap-1">
                      <strong>Rate per hour:</strong> $
                      {ratePerHour.toFixed(2)}
                    </div>
                    <div className="flex flex-row items-center gap-1">
                      <strong>Tuition (paid in full):</strong> $
                      {tuitionAmount.toFixed(2)}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-40 gap-4">
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="general"
              >
                <Contact size={16} /> General
              </RegisterFormMenu>
              {/* {id !== "new" && (
                <RegisterFormMenu
                  setActiveMenu={setActiveMenu}
                  activeMenu={activeMenu}
                  name="follow-up"
                >
                  <ListMinus size={16} /> Follow Up
                </RegisterFormMenu>
              )} */}
              {/* {id !== "new" && (
                <RegisterFormMenu
                  setActiveMenu={setActiveMenu}
                  activeMenu={activeMenu}
                  name="documents"
                >
                  <Files size={16} /> Documents
                </RegisterFormMenu>
              )} */}
              {pageData.name && (
                <div className="border-b w-full border-slate-500 text-slate-700">
                  Enrollment Flow
                </div>
              )}
              {pageData.name && newEnrollmentAvaiable && (
                <RegisterFormMenu
                  setActiveMenu={setActiveMenu}
                  activeMenu={activeMenu}
                  name="new-enrollment"
                >
                  <CirclePlay size={16} />
                  New Enrollment
                </RegisterFormMenu>
              )}
              {pageData.name && !newEnrollmentAvaiable && (
                <RegisterFormMenu
                  setActiveMenu={setActiveMenu}
                  activeMenu={activeMenu}
                  name="edit-enrollment"
                >
                  <Edit size={16} />
                  Active Enrollment
                </RegisterFormMenu>
              )}
              {pageData.name && (
                <RegisterFormMenu
                  setActiveMenu={setActiveMenu}
                  activeMenu={activeMenu}
                  name="enrollment-history"
                >
                  <History size={16} />
                  History
                </RegisterFormMenu>
              )}
            </div>
            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <InputContext.Provider
                value={{
                  id,
                  generalForm,
                  setSuccessfullyUpdated,
                  fullscreen,
                  setFullscreen,
                  successfullyUpdated,
                  handleCloseForm,
                  handleInactivate,
                  //   handleOutsideMail,
                  canceled: pageData.canceled_at,
                }}
              >
                <div
                  id="scrollPage"
                  ref={scrollContainerRef}
                  className="flex flex-col items-start justify-start text-sm overflow-y-scroll"
                >
                  <Form
                    ref={generalForm}
                    onSubmit={handleGeneralFormSubmit}
                    className="w-full"
                  >
                    <FormHeader
                      access={access}
                      loading={loading}
                      title={pageData.name + " " + pageData.last_name}
                      registry={registry}
                      InputContext={InputContext}
                      disabled={!pageData.processtype_id}
                      saveText={
                        activeMenu === "new-enrollment"
                          ? "Create Enrollment Process"
                          : "Save changes"
                      }
                      saveIcon={
                        activeMenu === "new-enrollment" ? "Play" : "Save"
                      }
                      saveColor={
                        activeMenu === "new-enrollment" ? "bg-emerald-500" : ""
                      }
                    />
                    {id === "new" || pageData.loaded ? (
                      <>
                        {activeMenu === "general" && (
                          <InputLineGroup
                            title="GENERAL"
                            activeMenu={activeMenu === "general"}
                          >
                            <FindGeneric
                              route="filials"
                              title="Filial"
                              scope="filial"
                              required
                              InputContext={InputContext}
                              defaultValue={
                                id === "new" && auth.filial.id !== 1
                                  ? {
                                      id: auth.filial.id,
                                      name: auth.filial.name,
                                    }
                                  : {
                                      id: pageData.filial?.id,
                                      name: pageData.filial?.name,
                                    }
                              }
                              fields={[
                                {
                                  title: "Name",
                                  name: "name",
                                },
                              ]}
                            />
                            <InputLine title="General Data">
                              <Input
                                type="hidden"
                                name="category"
                                required
                                grow
                                title="Category"
                                defaultValue="Prospect"
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                sanitize="person"
                                name="name"
                                required
                                grow
                                title="Name"
                                defaultValue={pageData.name}
                                InputContext={InputContext}
                              />
                              {/* <Input
                              type="text"
                              name="middle_name"
                              grow
                              title="Middle Name"
                              defaultValue={pageData.middle_name}
                              InputContext={InputContext}
                            /> */}
                              <Input
                                type="text"
                                sanitize="person"
                                name="last_name"
                                required
                                grow
                                title="Last Name"
                                defaultValue={pageData.last_name}
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine>
                              <SelectPopover
                                name="gender"
                                required
                                grow
                                title="Gender"
                                isSearchable
                                defaultValue={genderOptions.find(
                                  (gender) => gender.value === pageData.gender
                                )}
                                options={genderOptions}
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <FindGeneric
                              route="agents"
                              title="Responsible Agent"
                              scope="agent"
                              extraParams={{ active: true }}
                              required
                              InputContext={InputContext}
                              defaultValue={{
                                id:
                                  id === "new" && userAgent
                                    ? userAgent.id
                                    : pageData.agent?.id,
                                name:
                                  id === "new" && userAgent
                                    ? userAgent.name
                                    : pageData.agent?.name,
                              }}
                              fields={[
                                {
                                  title: "Associate Name",
                                  name: "name",
                                },
                              ]}
                            />
                            <FindGeneric
                              route="processtypes"
                              title="Type"
                              scope="processtypes"
                              readOnly
                              InputContext={InputContext}
                              defaultValue={{
                                id: pageData.processtypes?.id,
                                name: pageData.processtypes?.name,
                              }}
                              fields={[
                                {
                                  title: "Name",
                                  name: "name",
                                },
                              ]}
                            />
                            <FindGeneric
                              route="processsubstatuses"
                              title="Sub Status"
                              scope="processsubstatuses"
                              readOnly
                              InputContext={InputContext}
                              defaultValue={{
                                id: pageData.processsubstatuses?.id,
                                name: pageData.processsubstatuses?.name,
                              }}
                              fields={[
                                {
                                  title: "Name",
                                  name: "name",
                                },
                              ]}
                            />
                            <FindGeneric
                              route="partners_and_influencers"
                              title="Partner and Influencer"
                              scope="partners_and_influencers"
                              readOnly
                              InputContext={InputContext}
                              defaultValue={{
                                id: pageData.partners_and_influencers?.id,
                                partners_name:
                                  pageData.partners_and_influencers
                                    ?.partners_name,
                              }}
                              fields={[
                                {
                                  title: "Name",
                                  name: "partners_name",
                                },
                              ]}
                            />

                            <PricesSimulation
                              student={pageData}
                              InputContext={InputContext}
                              FullGridContext={FullGridContext}
                              generalForm={generalForm}
                              showAdmissionDiscounts={true}
                              isAdmissionDiscountChangable={true}
                              showFinancialDiscounts={false}
                              isFinancialDiscountChangable={false}
                            />

                            {(() => {
                              const session =
                                pageData?.contracts?.[0]?.sessions?.[0];
                              if (!session) return null;
                              if (session.pricing_mode !== "hourly") return null;

                              const totalHours = session.total_hours ?? 0;
                              const ratePerHour = session.rate_per_hour ?? 0;
                              const tuitionAmount = session.tuition_amount ?? 0;
                              const formatSessionDate = (value) => {
                                if (!value) return "-";
                                const parsed = parseISO(value);
                                return isValid(parsed)
                                  ? format(parsed, "MM/dd/yyyy")
                                  : value;
                              };
                              return (
                                <InputLine title="Global Session (Hourly)">
                                  <div className="w-full flex flex-col gap-2 text-xs bg-blue-50 border border-blue-200 rounded-md p-3">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Period:</span>
                                      <span className="font-medium">
                                        {formatSessionDate(
                                          session.session_start_date
                                        )}{" "}
                                        →{" "}
                                        {formatSessionDate(
                                          session.session_end_date
                                        )}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Total hours:</span>
                                      <span className="font-medium">{totalHours}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Rate per hour:</span>
                                      <span className="font-medium">
                                        ${ratePerHour.toFixed(2)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between border-t pt-1 font-semibold">
                                      <span>Tuition (paid in full):</span>
                                      <span>${tuitionAmount.toFixed(2)}</span>
                                    </div>
                                  </div>
                                </InputLine>
                              );
                            })()}

                            <InputLine title="Contact">
                              <Input
                                type="text"
                                sanitize="email"
                                name="email"
                                required
                                title="E-mail"
                                grow
                                defaultValue={pageData.email}
                                InputContext={InputContext}
                              />
                              <PhoneNumberInput
                                type="text"
                                grow
                                name="whatsapp"
                                title="Whatsapp"
                                isPhoneNumber
                                value={normalizePhoneForInput(pageData.whatsapp)}
                                InputContext={InputContext}
                              />
                              <PhoneNumberInput
                                type="text"
                                grow
                                name="phone"
                                title="U.S. Phone"
                                value={normalizePhoneForInput(pageData.phone)}
                                InputContext={InputContext}
                              />
                              <PhoneNumberInput
                                type="text"
                                grow
                                name="home_country_phone"
                                title="Home Country Phone"
                                value={normalizePhoneForInput(
                                  pageData.home_country_phone
                                )}
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine>
                              <SelectPopover
                                id="preferred_contact_form"
                                name="preferred_contact_form"
                                grow
                                title="Preferred Contact Form"
                                isSearchable
                                defaultValue={optionsContactForm.find(
                                  (opt) =>
                                    opt.value ===
                                    pageData.preferred_contact_form
                                )}
                                options={optionsContactForm}
                                onMenuOpen={handlePreferredContactOpen}
                                InputContext={InputContext}
                              />
                            </InputLine>
                          </InputLineGroup>
                        )}

                        {activeMenu === "new-enrollment" && (
                          <InputLineGroup
                            title={`${
                              newEnrollmentAvaiable
                                ? "New Enrollment"
                                : "Enrollment"
                            }`}
                            activeMenu={activeMenu === "new-enrollment"}
                          >
                            <FindGeneric
                              route="agents"
                              title="Responsible Agent"
                              scope="agent"
                              extraParams={{ active: true }}
                              required
                              InputContext={InputContext}
                              setReturnFindGeneric={(agent) => {
                                setPartnerAndInfluencer({
                                  id: null,
                                  partners_name: null,
                                });
                                setTimeout(() => {
                                  setPartnerAndInfluencer(
                                    agent?.partners_and_influencer
                                  );
                                }, 100);
                              }}
                              defaultValue={{
                                id: userAgent
                                  ? userAgent.id
                                  : pageData.agent?.id,
                                name: userAgent
                                  ? userAgent.name
                                  : pageData.agent?.name,
                              }}
                              fields={[
                                {
                                  title: "Name",
                                  name: "name",
                                },
                              ]}
                            />
                            <FindGeneric
                              route="processtypes"
                              title="Type"
                              scope="processtypes"
                              required
                              InputContext={InputContext}
                              setReturnFindGeneric={(row) => {
                                setType(row?.id || null);
                                setSelectedTypeName(row?.name || null);
                                setPageData({
                                  ...pageData,
                                  searchFields: {
                                    ...pageData.searchFields,
                                    processtype_id: row?.id,
                                  },
                                });
                              }}
                              defaultValue={{
                                id: null,
                                name: null,
                              }}
                              fields={[
                                {
                                  title: "Name",
                                  name: "name",
                                },
                              ]}
                            />
                            <FindGeneric
                              route="processsubstatuses"
                              title="Sub Status"
                              scope="processsubstatuses"
                              required
                              InputContext={InputContext}
                              setReturnFindGeneric={(substatus) => {
                                setSelectedSubStatusName(
                                  substatus?.name || null
                                );
                                setSelectedSubStatusId(substatus?.id || null);
                                setPageData({
                                  ...pageData,
                                  searchFields: {
                                    ...pageData.searchFields,
                                    processsubstatus_id: substatus.id,
                                  },
                                });
                              }}
                              defaultValue={{
                                id: null,
                                name: null,
                              }}
                              fields={[
                                {
                                  title: "Name",
                                  name: "name",
                                },
                              ]}
                              extraParams={type ? { processtype_id: type } : null}
                              readOnly={!type}
                              onAttemptWithoutDep={!type ? () => toast.warn("Please select a Type before choosing a Sub Status.", { transition: Zoom }) : null}
                            />

                            {partnerAndInfluencer?.id !== null && (
                              <FindGeneric
                                route="partners_and_influencers"
                                title="Partner and Influencer"
                                scope="partners_and_influencers"
                                InputContext={InputContext}
                                defaultValue={{
                                  id: partnerAndInfluencer?.id,
                                  partners_name:
                                    partnerAndInfluencer?.partners_name,
                                }}
                                fields={[
                                  {
                                    title: "Name",
                                    name: "partners_name",
                                  },
                                ]}
                              />
                            )}

                            {showSessionFields && (
                              <>
                                <InputLine title="Billing Mode">
                                  <SelectPopover
                                    name="pricing_mode_display"
                                    grow
                                    required
                                    title="Billing Mode"
                                    options={[
                                      { value: "fixed", label: "Fixed price" },
                                      { value: "hourly", label: "Per hour" },
                                    ]}
                                    defaultValue={{ value: "fixed", label: "Fixed price" }}
                                    onChange={(el) =>
                                      setPricingMode(el?.value || "fixed")
                                    }
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                                <InputLine title="Session">
                                  <ProspectDateField
                                    name="session_start_date"
                                    grow
                                    required
                                    title="Session Start Date"
                                    InputContext={InputContext}
                                    onChange={setSessionStartDate}
                                  />
                                  <ProspectDateField
                                    name="session_end_date"
                                    grow
                                    required
                                    title="Session End Date"
                                    InputContext={InputContext}
                                    onChange={setSessionEndDate}
                                  />
                                  <GlobalTuitionDueDate
                                    Field={ProspectDateField}
                                    sessionStartDate={sessionStartDate}
                                    InputContext={InputContext}
                                    onChange={setFirstTuitionDueDate}
                                  />
                                </InputLine>
                                <InputLine>
                                  <SelectPopover
                                    name="plan_schedule"
                                    grow
                                    required={pricingMode === "hourly"}
                                    title="Plan Schedule"
                                    isSearchable
                                    options={planScheduleOptions}
                                    onChange={(el) =>
                                      setPlanScheduleId(el?.value || null)
                                    }
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                                {pricingMode === "fixed" && (
                                  <InputLine title="Payment">
                                    <SelectPopover
                                      name="payment_type_display"
                                      grow
                                      required
                                      title="Payment Type"
                                      options={[
                                        { value: "full", label: "Full payment" },
                                        { value: "installments", label: "3x installments" },
                                      ]}
                                      defaultValue={{ value: "full", label: "Full payment" }}
                                      onChange={(el) => setPaymentType(el?.value || "full")}
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                )}
                                {/* Hourly is always paid in full — a single
                                    receivable, so no installment option. */}
                              </>
                            )}

                            <PricesSimulation
                              student={pageData}
                              InputContext={InputContext}
                              FullGridContext={FullGridContext}
                              generalForm={generalForm}
                              showAdmissionDiscounts={true}
                              isAdmissionDiscountChangable={true}
                              showFinancialDiscounts={false}
                              isFinancialDiscountChangable={false}
                              onTotalsChange={
                                showSessionFields ? setSimulatedTotals : null
                              }
                            />
                            {showSessionFields && pricingMode === "hourly" && hourlyPreview && (() => {
                              // Hourly (Global): hours, rate and tuition all
                              // come from the backend preview — never computed
                              // here. Charged in full (single receivable).
                              const regFee = pageData?.priceLists?.registration_fee || 0;
                              const totalHours = hourlyPreview.total_hours || 0;
                              const ratePerHour = hourlyPreview.rate_per_hour || 0;
                              const tuition = hourlyPreview.tuition || 0;
                              const grandTotal = regFee + tuition;
                              return (
                                <InputLine title="Payment Summary">
                                  <div className="w-full flex flex-col gap-2 text-xs bg-blue-50 border border-blue-200 rounded-md p-3">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Total hours:</span>
                                      <span className="font-medium">{totalHours}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Rate per hour:</span>
                                      <span className="font-medium">${ratePerHour.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Registration Fee:</span>
                                      <span className="font-medium">${regFee.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Tuition:</span>
                                      <span className="font-medium">${tuition.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between border-t pt-1 font-semibold">
                                      <span>Grand Total (paid in full):</span>
                                      <span>${grandTotal.toFixed(2)}</span>
                                    </div>
                                  </div>
                                </InputLine>
                              );
                            })()}
                            {showSessionFields && pricingMode === "fixed" && pageData?.priceLists && (() => {
                              const regFee = pageData?.priceLists?.registration_fee || 0;
                              const tuitionOriginal = pageData?.priceLists?.tuition || 0;
                              const totalDiscount = simulatedTotals?.totalDiscount || 0;
                              const tuitionAfterDiscount = Math.max(0, tuitionOriginal - totalDiscount);
                              const grandTotal =
                                simulatedTotals?.totalTuition ??
                                tuitionOriginal + regFee + (pageData?.priceLists?.book || 0);
                              const installmentBase = parseFloat((tuitionAfterDiscount / 3).toFixed(2));
                              const remainder = parseFloat((tuitionAfterDiscount - installmentBase * 3).toFixed(2));
                              return (
                                <InputLine title="Payment Summary">
                                  <div className="w-full flex flex-col gap-2 text-xs bg-blue-50 border border-blue-200 rounded-md p-3">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Registration Fee:</span>
                                      <span className="font-medium">${regFee.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Tuition:</span>
                                      <span className="font-medium">${tuitionOriginal.toFixed(2)}</span>
                                    </div>
                                    {totalDiscount > 0 && (
                                      <div className="flex justify-between text-green-700">
                                        <span>Discount:</span>
                                        <span className="font-medium">-${totalDiscount.toFixed(2)}</span>
                                      </div>
                                    )}
                                    <div className="flex justify-between border-t pt-1 font-semibold">
                                      <span>Grand Total:</span>
                                      <span>${grandTotal.toFixed(2)}</span>
                                    </div>
                                    {paymentType === "installments" && (
                                      <div className="mt-1 border-t pt-2 flex flex-col gap-1 text-gray-600">
                                        <span className="font-semibold text-gray-700">Installment Preview (Tuition only):</span>
                                        <div className="flex justify-between">
                                          <span>1st (session start):</span>
                                          <span>${(installmentBase + remainder).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>2nd (mid-session):</span>
                                          <span>${installmentBase.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>3rd (session end):</span>
                                          <span>${installmentBase.toFixed(2)}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </InputLine>
                              );
                            })()}
                          </InputLineGroup>
                        )}
                        {activeMenu === "edit-enrollment" && (
                          <InputLineGroup
                            title="Active Enrollment"
                            activeMenu={activeMenu === "edit-enrollment"}
                          >
                            <FindGeneric
                              route="agents"
                              title="Responsible Agent"
                              scope="agent"
                              required
                              readOnly
                              InputContext={InputContext}
                              defaultValue={{
                                id: pageData.agent?.id,
                                name: pageData.agent?.name,
                              }}
                              fields={[
                                {
                                  title: "Name",
                                  name: "name",
                                },
                              ]}
                            />
                            <FindGeneric
                              route="processtypes"
                              title="Type"
                              scope="processtypes"
                              required
                              readOnly
                              InputContext={InputContext}
                              defaultValue={{
                                id: pageData.processtypes?.id,
                                name: pageData.processtypes?.name,
                              }}
                              fields={[
                                {
                                  title: "Name",
                                  name: "name",
                                },
                              ]}
                            />
                            <FindGeneric
                              route="processsubstatuses"
                              title="Sub Status"
                              scope="processsubstatuses"
                              required
                              readOnly
                              InputContext={InputContext}
                              defaultValue={{
                                id: pageData.processsubstatuses?.id,
                                name: pageData.processsubstatuses?.name,
                              }}
                              fields={[
                                {
                                  title: "Name",
                                  name: "name",
                                },
                              ]}
                            />

                            <FindGeneric
                              route="partners_and_influencers"
                              title="Partner and Influencer"
                              scope="partners_and_influencers"
                              InputContext={InputContext}
                              readOnly
                              defaultValue={{
                                id: pageData.partners_and_influencers?.id,
                                partners_name:
                                  pageData.partners_and_influencers
                                    ?.partners_name,
                              }}
                              fields={[
                                {
                                  title: "Name",
                                  name: "partners_name",
                                },
                              ]}
                            />

                            {!showEditSessionFields && (
                              <>
                                <PricesSimulation
                                  student={pageData}
                                  InputContext={InputContext}
                                  FullGridContext={FullGridContext}
                                  generalForm={generalForm}
                                  showAdmissionDiscounts={true}
                                  isAdmissionDiscountChangable={true}
                                  showFinancialDiscounts={false}
                                  isFinancialDiscountChangable={false}
                                />
                                <InputLine>
                                  <p className="text-xs text-gray-500 text-left">
                                    Save changes to keep these discounts. Existing
                                    invoices can then be updated in Enrollments.
                                  </p>
                                </InputLine>
                              </>
                            )}
                            {showEditSessionFields && editSession ? (
                              <>
                                <InputLine title="Billing Mode">
                                  <SelectPopover
                                    key={`edit-pricing-${activeSession.id}`}
                                    name="edit_pricing_mode"
                                    grow
                                    required
                                    title="Billing Mode"
                                    options={[
                                      { value: "fixed", label: "Fixed price" },
                                      { value: "hourly", label: "Per hour" },
                                    ]}
                                    defaultValue={
                                      editSession.pricing_mode === "hourly"
                                        ? { value: "hourly", label: "Per hour" }
                                        : {
                                            value: "fixed",
                                            label: "Fixed price",
                                          }
                                    }
                                    onChange={(el) =>
                                      setEditSessionField(
                                        "pricing_mode",
                                        el?.value || "fixed"
                                      )
                                    }
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                                <InputLine title="Session">
                                  <ProspectDateField
                                    name="edit_session_start_date"
                                    grow
                                    required
                                    title="Session Start Date"
                                    defaultValue={
                                      editSession.session_start_date
                                    }
                                    InputContext={InputContext}
                                    onChange={(value) =>
                                      setEditSessionField(
                                        "session_start_date",
                                        value
                                      )
                                    }
                                  />
                                  <ProspectDateField
                                    name="edit_session_end_date"
                                    grow
                                    required
                                    title="Session End Date"
                                    defaultValue={editSession.session_end_date}
                                    InputContext={InputContext}
                                    onChange={(value) =>
                                      setEditSessionField(
                                        "session_end_date",
                                        value
                                      )
                                    }
                                  />
                                  <ProspectDateField
                                    name="first_tuition_due_date"
                                    grow required title="First Tuition Due Date"
                                    defaultValue={editSession.first_tuition_due_date}
                                    InputContext={InputContext}
                                    onChange={(value) => setEditSessionField("first_tuition_due_date", value)}
                                  />
                                </InputLine>
                                <InputLine>
                                  <SelectPopover
                                    key={`edit-schedule-${activeSession.id}-${editPlanScheduleOptions.length}`}
                                    name="edit_plan_schedule"
                                    grow
                                    required={
                                      editSession.pricing_mode === "hourly"
                                    }
                                    title="Plan Schedule"
                                    isSearchable
                                    options={editPlanScheduleOptions}
                                    defaultValue={editPlanScheduleOptions.find(
                                      (option) =>
                                        option.value ===
                                        editSession.plan_schedule_id
                                    )}
                                    onChange={(el) =>
                                      setEditSessionField(
                                        "plan_schedule_id",
                                        el?.value || null
                                      )
                                    }
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                                {editSession.pricing_mode === "fixed" ? (
                                  <InputLine title="Payment">
                                    <SelectPopover
                                      key={`edit-payment-${activeSession.id}`}
                                      name="edit_payment_type"
                                      grow
                                      required
                                      title="Payment Type"
                                      options={[
                                        {
                                          value: "full",
                                          label: "Full payment",
                                        },
                                        {
                                          value: "installments",
                                          label: "3x installments",
                                        },
                                      ]}
                                      defaultValue={
                                        editSession.payment_type ===
                                        "installments"
                                          ? {
                                              value: "installments",
                                              label: "3x installments",
                                            }
                                          : {
                                              value: "full",
                                              label: "Full payment",
                                            }
                                      }
                                      onChange={(el) =>
                                        setEditSessionField(
                                          "payment_type",
                                          el?.value || "full"
                                        )
                                      }
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                ) : (
                                  <InputLine title="Payment">
                                    <div className="flex flex-col justify-center items-start grow">
                                      <div className="px-1 text-xs">
                                        Payment Type
                                      </div>
                                      <div className="text-sm text-left py-1 text-gray-500">
                                        Full payment{" "}
                                        <span className="text-xs text-gray-400">
                                          (hourly is always charged in full)
                                        </span>
                                      </div>
                                    </div>
                                    <div className="flex flex-col justify-center items-start grow">
                                      <div className="px-1 text-xs">
                                        Tuition
                                      </div>
                                      <div className="text-sm text-left py-1">
                                        {editHourlyPreview ? (
                                          <>
                                            {editHourlyPreview.total_hours}h x $
                                            {parseFloat(
                                              editHourlyPreview.rate_per_hour ||
                                                0
                                            ).toFixed(2)}
                                            /h ={" "}
                                            <strong>
                                              $
                                              {parseFloat(
                                                editHourlyPreview.tuition || 0
                                              ).toFixed(2)}
                                            </strong>
                                          </>
                                        ) : (
                                          <span className="text-gray-500">
                                            $
                                            {parseFloat(
                                              activeSession.tuition_amount || 0
                                            ).toFixed(2)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </InputLine>
                                )}
                                <InputLine>
                                  <p className="text-xs text-gray-500 text-left">
                                    Saving cancels the current tuition invoices
                                    and generates new ones. It is blocked if any
                                    of them has already been settled.
                                  </p>
                                </InputLine>
                                <PricesSimulation
                                  student={pageData}
                                  InputContext={InputContext}
                                  FullGridContext={FullGridContext}
                                  generalForm={generalForm}
                                  showAdmissionDiscounts={true}
                                  isAdmissionDiscountChangable={true}
                                  showFinancialDiscounts={false}
                                  isFinancialDiscountChangable={false}
                                  onTotalsChange={setEditSimulatedTotals}
                                />
                                <GlobalSessionSummary
                                  firstTuitionDueDate={editSession?.first_tuition_due_date}
                                  session={activeSession}
                                  invoices={activeInvoices}
                                  priceLists={pageData?.priceLists}
                                  simulatedTotals={editSimulatedTotals}
                                  hourlyPreview={editHourlyPreview}
                                  pricingMode={editSession.pricing_mode}
                                  paymentType={editSession.payment_type}
                                  sessionStartDate={
                                    editSession.session_start_date
                                  }
                                  sessionEndDate={editSession.session_end_date}
                                />
                              </>
                            ) : null}
                          </InputLineGroup>
                        )}

                        {activeMenu === "enrollment-history" && (
                          <InputLineGroup
                            title="Enrollment History"
                            activeMenu={activeMenu === "enrollment-history"}
                          >
                            <div className="w-full flex flex-col justify-center items-start gap-4">
                              {pageData?.contracts?.length === 0 ? (
                                <InputLine title="Contracts">
                                  <div className="mt-2 text-xs text-gray-500">
                                    No contracts yet.
                                  </div>
                                </InputLine>
                              ) : (
                                pageData?.contracts?.map((contract, index) => {
                                  return (
                                    <Scope
                                      key={index}
                                      path={`contract.${index}`}
                                    >
                                      <InputLine
                                        title={
                                          index === 0
                                            ? "Contracts (" +
                                              pageData.contracts.length +
                                              ")"
                                            : ""
                                        }
                                      >
                                        <div className="mt-2 text-xs text-gray-500">
                                          #{pageData.contracts.length - index}
                                        </div>

                                        <Input
                                          type="text"
                                          name="date"
                                          readOnly
                                          title="Date"
                                          shrink
                                          defaultValue={format(
                                            parseISO(contract.date),
                                            "MM/dd/yyyy"
                                          )}
                                          InputContext={InputContext}
                                        />
                                        <Input
                                          type="text"
                                          name="application"
                                          readOnly
                                          title="Application"
                                          grow
                                          defaultValue={
                                            contract.enrollment.application
                                          }
                                          InputContext={InputContext}
                                        />
                                        <Input
                                          type="text"
                                          name="processtypes"
                                          readOnly
                                          title="Process Type"
                                          grow
                                          defaultValue={
                                            contract?.processtypes?.name
                                          }
                                          InputContext={InputContext}
                                        />
                                        <Input
                                          type="text"
                                          name="processsubstatuses"
                                          readOnly
                                          title="Process Substatus"
                                          grow
                                          defaultValue={
                                            contract?.processsubstatuses?.name
                                          }
                                          InputContext={InputContext}
                                        />
                                        <Input
                                          type="text"
                                          name="status"
                                          readOnly
                                          title="Status"
                                          grow
                                          defaultValue={contract.status}
                                          InputContext={InputContext}
                                        />
                                      </InputLine>
                                    </Scope>
                                  );
                                })
                              )}
                            </div>
                          </InputLineGroup>
                        )}
                      </>
                    ) : (
                      <FormLoading />
                    )}
                  </Form>
                </div>
              </InputContext.Provider>
            </div>
          </div>
        )
      ) : null}
    </Preview>
  );
}
